import { Model } from '../../_kernel/architecture'
import { z } from 'zod'
import { ListOfRating } from '../../_kernel/ListOfRating'
import { ListOfStars } from '../../_kernel/ListOfStars'
import { ListOfFacility } from '../../property/domain/ListOfFacility'
import { ListOfAccommodation } from '../../_kernel/ListOfAccommodation'
import { Count } from '../../_kernel/Count'
import { ListOfTheme } from './ListOfTheme'
import { ListOfBoard } from './ListOfBoard'
import { ListOfPaymentOption } from './ListOfPaymentOption'
import { ListOfCancellationPolicy } from './ListOfCancellationPolicy'

export const SearchFiltersValidation = z.object({
  ratings: z.instanceof(ListOfRating, { message: '[SearchFilters] ratings is required' }),
  stars: z.instanceof(ListOfStars, { message: '[SearchFilters] stars is required' }),
  facilities: z.instanceof(ListOfFacility, { message: '[SearchFilters] facilities is required' }),
  accommodationTypes: z.instanceof(ListOfAccommodation, {
    message: '[SearchFilters] accommodation types is required',
  }),
  themes: z.instanceof(ListOfTheme, { message: '[SearchFilters] themes is required' }),
  minPrice: z.instanceof(Count).optional(),
  maxPrice: z.instanceof(Count).optional(),
  boards: z.instanceof(ListOfBoard, { message: '[SearchFilters] boards is required' }),
  paymentOptions: z.instanceof(ListOfPaymentOption, {
    message: '[SearchFilters] payment options is required',
  }),
  cancellationPolicies: z.instanceof(ListOfCancellationPolicy, {
    message: '[SearchFilters] cancellation policies is required',
  }),
})

export class SearchFilters extends Model {
  static create({
    ratings,
    stars,
    accommodationTypes,
    facilities,
    themes,
    minPrice,
    maxPrice,
    boards,
    paymentOptions,
    cancellationPolicies,
  }: z.infer<typeof SearchFiltersValidation>): SearchFilters {
    SearchFiltersValidation.parse({
      ratings,
      stars,
      facilities,
      accommodationTypes,
      themes,
      minPrice,
      maxPrice,
      boards,
      paymentOptions,
      cancellationPolicies,
    })

    return new SearchFilters(
      ratings,
      stars,
      facilities,
      accommodationTypes,
      themes,
      boards,
      paymentOptions,
      cancellationPolicies,
      minPrice,
      maxPrice
    )
  }

  static fromPrimitives(filters: {
    ratings?: string[] | number[]
    accommodationTypes?: string[]
    facilities?: string[]
    themes?: string[]
    minPrice?: string | number
    maxPrice?: string | number
    boards?: string[]
    stars?: string[]
    paymentOptions?: string[]
    cancellationPolicies?: string[]
  }): SearchFilters {
    return SearchFilters.create({
      ratings: ListOfRating.fromPrimitives({ ratings: filters.ratings }),
      stars: ListOfStars.fromPrimitives({ stars: filters.stars }),
      facilities: ListOfFacility.fromJSONOrEmpty({
        json: {
          list: filters.facilities?.map(f => ({ id: f, codeGroup: f })) || [],
        },
      }),
      accommodationTypes: ListOfAccommodation.fromPrimitives({
        accommodations: filters.accommodationTypes,
      }),
      themes: ListOfTheme.fromJSON({
        json: { list: filters.themes?.map(t => ({ name: t, slug: t })) || [] },
      }),
      ...(filters.minPrice && { minPrice: Count.create({ value: Number(filters.minPrice) }) }),
      ...(filters.maxPrice && { maxPrice: Count.create({ value: Number(filters.maxPrice) }) }),
      boards: ListOfBoard.fromJSON({
        json: { list: filters.boards?.map(b => ({ name: b, slug: b })) || [] },
      }),
      paymentOptions: ListOfPaymentOption.fromJSON({
        json: { list: filters.paymentOptions?.map(p => ({ name: p, slug: p })) || [] },
      }),
      cancellationPolicies: ListOfCancellationPolicy.fromJSON({
        json: { list: filters.cancellationPolicies?.map(c => ({ name: c, slug: c })) || [] },
      }),
    })
  }

  static fromJSON(json: ReturnType<SearchFilters['toJSON']>): SearchFilters {
    return SearchFilters.create({
      ratings: ListOfRating.fromJSON({ json: json.ratings }),
      stars: ListOfStars.fromJSON({ json: json.stars }),
      facilities: ListOfFacility.fromJSON({ json: json.facilities }),
      accommodationTypes: ListOfAccommodation.fromJSON({ json: json.accommodationTypes }),
      themes: ListOfTheme.fromJSON({ json: json.themes }),
      ...(json.minPrice && { minPrice: Count.create(json.minPrice) }),
      ...(json.maxPrice && { maxPrice: Count.create(json.maxPrice) }),
      boards: ListOfBoard.fromJSON({ json: json.boards }),
      paymentOptions: ListOfPaymentOption.fromJSON({ json: json.paymentOptions }),
      cancellationPolicies: ListOfCancellationPolicy.fromJSON({ json: json.cancellationPolicies }),
    })
  }

  static default(): SearchFilters {
    return SearchFilters.create({
      ratings: ListOfRating.fromJSON({ json: { list: [] } }),
      stars: ListOfStars.fromJSON({ json: { list: [] } }),
      facilities: ListOfFacility.fromJSON({ json: { list: [] } }),
      accommodationTypes: ListOfAccommodation.fromJSON({ json: { list: [] } }),
      themes: ListOfTheme.fromJSON({ json: { list: [] } }),
      boards: ListOfBoard.fromJSON({ json: { list: [] } }),
      paymentOptions: ListOfPaymentOption.fromJSON({ json: { list: [] } }),
      cancellationPolicies: ListOfCancellationPolicy.fromJSON({ json: { list: [] } }),
    })
  }

  constructor(
    public readonly ratings: ListOfRating,
    public readonly stars: ListOfStars,
    public readonly facilities: ListOfFacility,
    public readonly accommodationTypes: ListOfAccommodation,
    public readonly themes: ListOfTheme,
    public readonly boards: ListOfBoard,
    public readonly paymentOptions: ListOfPaymentOption,
    public readonly cancellationPolicies: ListOfCancellationPolicy,
    public readonly minPrice?: Count,
    public readonly maxPrice?: Count
  ) {
    super()
  }

  toJSON() {
    return {
      ratings: this.ratings.toJSON(),
      stars: this.stars.toJSON(),
      facilities: this.facilities.toJSON(),
      accommodationTypes: this.accommodationTypes.toJSON(),
      themes: this.themes.toJSON(),
      ...(this.minPrice && { minPrice: this.minPrice.toJSON() }),
      ...(this.maxPrice && { maxPrice: this.maxPrice.toJSON() }),
      boards: this.boards.toJSON(),
      paymentOptions: this.paymentOptions.toJSON(),
      cancellationPolicies: this.cancellationPolicies.toJSON(),
    }
  }

  toPrimitives() {
    return {
      ratings: this.ratings.map(r => r.rating).filter(item => item !== undefined) as number[],
      stars: this.stars.map(s => s.value.toString()),
      facilities: this.facilities.map(f => f.codeGroup),
      accommodationTypes: this.accommodationTypes.map(a => a.value.toLowerCase()),
      themes: this.themes.map(t => t.slug),
      ...(this.minPrice && { minPrice: this.minPrice.value }),
      ...(this.maxPrice && { maxPrice: this.maxPrice.value }),
      boards: this.boards.map(b => b.slug),
      paymentOptions: this.paymentOptions.map(p => p.slug),
      cancellationPolicies: this.cancellationPolicies.map(c => c.slug),
    }
  }
}
